import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useTriggerLoad } from "@redotech/react-util/load";
import { useStore } from "@redotech/react-util/store";
import { TagKind } from "@redotech/redo-common-model-tags-common/tag";
import { FulfillmentGroupData } from "@redotech/redo-fulfillment-group-common/fulfillment-group";
import {
  canPurchaseLabelStatus,
  FulfillmentGroupStatus,
  isEditingLockedStatus,
} from "@redotech/redo-fulfillment-group-common/fulfillment-group-status";
import { ShopifyFulfillmentHoldDisplayNames } from "@redotech/redo-fulfillment-group-common/shopify-fulfillment-schemas";
import { Preset } from "@redotech/redo-fulfillment-parcel-common/presets";
import DotIcon from "@redotech/redo-icons/dot-01.svg";
import { GlobalAgentContext } from "@redotech/redo-merchant-app-common/global-agent-context";
import { trackerStatusColors } from "@redotech/redo-merchant-app-common/order";
import { OrderDetailsInfo } from "@redotech/redo-merchant-app-common/order-details-info";
import { RedoMerchantRpcClientContext } from "@redotech/redo-merchant-app-common/rpc-client";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import { FulfillmentGroupUpdate } from "@redotech/redo-merchant-fulfillment-rpc/schema/update-fulfillment-groups";
import { prettyTrackerStatus } from "@redotech/redo-order-tracking-trackable-common/delivery-estimation";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import { TeamContext } from "@redotech/redo-web/context/team-context";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { Store } from "@redotech/util/store";
import { localStore } from "@redotech/web-util/storage";
import {
  Component,
  memo,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import DraggableList from "react-draggable-list";
import { EditAddressModalProvider } from "../../../common/edit-address/edit-address-context";
import { MissingItemsModalProvider } from "../../../common/missing-items/missing-items-modal-context";
import { PackingSlipBinModalProvider } from "../../../common/packing-slip-bin-modal-provider";
import { PinVerificationProvider } from "../../../common/pin-verification-context";
import { FulfillmentGroupStatusBadge } from "../../../common/status-badge";
import { TagList } from "../../../common/tag-list";
import { useFulfillmentGroupPermissions } from "../../../utils/use-assigned-fulfillment-service";
import { Tags } from "../../cells/tags-cell";
import { getMarketplaceOrderTags } from "../../marketplace-order-tags";
import { ItemsCard } from "./items-card";
import { NotesCard } from "./notes-card";
import * as styles from "./outbound-order.module.css";
import { ParcelPhotosCard } from "./parcel-photos-card";
import { PaymentInformationCard } from "./payment-information-card";
import { RedoContentCard, RedoContentCardProps } from "./redo-content-card";

const defaultShipmentDetailsCardOrder = [
  "items",
  "notes",
  "payment-information",
  "parcel-photos",
] as const;
const shipmentDetailsCardDragPaddingPx = 24;

type ShipmentDetailsCardId = (typeof defaultShipmentDetailsCardOrder)[number];

function getDefaultShipmentDetailsCardOrder(): ShipmentDetailsCardId[] {
  return [...defaultShipmentDetailsCardOrder];
}

function normalizeShipmentDetailsCardOrder(
  value: unknown,
): ShipmentDetailsCardId[] {
  if (!Array.isArray(value)) {
    return getDefaultShipmentDetailsCardOrder();
  }

  const validCardIds = new Set<string>(defaultShipmentDetailsCardOrder);
  const storedCardOrder = value.filter(
    (cardId): cardId is ShipmentDetailsCardId =>
      typeof cardId === "string" && validCardIds.has(cardId),
  );
  return [
    ...storedCardOrder,
    ...defaultShipmentDetailsCardOrder.filter(
      (cardId) => !storedCardOrder.includes(cardId),
    ),
  ];
}

function createShipmentDetailsCardOrderStore(
  userId?: string,
): Store<ShipmentDetailsCardId[]> {
  const baseKey = "redo.shipment-details-card-order";
  const storage = localStore(userId ? `${baseKey}.${userId}` : baseKey);

  return {
    get: () => {
      try {
        const stored = storage.get();
        if (!stored) {
          return getDefaultShipmentDetailsCardOrder();
        }
        return normalizeShipmentDetailsCardOrder(JSON.parse(stored));
      } catch {
        return getDefaultShipmentDetailsCardOrder();
      }
    },
    set: (value) => {
      storage.set(JSON.stringify(normalizeShipmentDetailsCardOrder(value)));
    },
  };
}

interface ShipmentDetailsCardData {
  id: ShipmentDetailsCardId;
  renderCard: (
    reorderHandleProps: RedoContentCardProps["reorderHandleProps"],
  ) => ReactNode;
}

class DraggableShipmentDetailsCard extends Component<{
  item: ShipmentDetailsCardData;
  dragHandleProps: RedoContentCardProps["reorderHandleProps"];
}> {
  override render() {
    return (
      <Flex w="full">
        {this.props.item.renderCard(this.props.dragHandleProps)}
      </Flex>
    );
  }
}

export const OutboundOrderFulfillmentGroup = memo(
  function OutboundOrderFulfillmentGroup({
    fulfillmentGroup,
    index,
    presets,
    setRefreshSymbol,
    totalGroups,
    handleUpdates,
    orderInfo,
    showMainFulfillmentForm = true,
  }: {
    fulfillmentGroup: FulfillmentGroupData;
    index: number;
    presets: Preset[];
    setRefreshSymbol: (symbol: symbol) => void;
    totalGroups: number;
    handleUpdates: (updates: FulfillmentGroupUpdate[]) => void;
    orderInfo: OrderDetailsInfo;
    showMainFulfillmentForm?: boolean;
  }) {
    const user = useRequiredContext(UserContext);
    const shipmentDetailsCardOrderStore = useMemo(
      () => createShipmentDetailsCardOrderStore(user._id),
      [user._id],
    );
    const [shipmentDetailsCardOrder, setShipmentDetailsCardOrder] = useStore(
      shipmentDetailsCardOrderStore,
    );
    const selectedStatus = fulfillmentGroup.status;

    useShipmentPageContext(fulfillmentGroup);

    const { permissionsToEditEveryGroup } = useFulfillmentGroupPermissions([
      fulfillmentGroup,
    ]);

    const isEditingLocked = isEditingLockedStatus(fulfillmentGroup.status);

    const team = useRequiredContext(TeamContext);

    const showParcelPhotos =
      team?.settings.outboundLabels.verifyParcelPhotos?.enabled ?? false;

    const handleShipmentDetailsCardReorder = useHandler(
      (newList: readonly ShipmentDetailsCardData[]) => {
        const newOrder = newList.map((card) => card.id);
        setShipmentDetailsCardOrder([
          ...newOrder,
          ...defaultShipmentDetailsCardOrder.filter(
            (cardId) => !newOrder.includes(cardId),
          ),
        ]);
      },
    );

    const shipmentDetailsCardRenderers: Record<
      ShipmentDetailsCardId,
      (
        reorderHandleProps: RedoContentCardProps["reorderHandleProps"],
      ) => ReactNode
    > = {
      items: (reorderHandleProps) => (
        <ItemsCard
          everythingDisabled={!permissionsToEditEveryGroup}
          fulfillmentGroup={fulfillmentGroup}
          reorderHandleProps={reorderHandleProps}
          showReorderHandle
          triggerReload={async () => {
            setRefreshSymbol(Symbol());
          }}
        />
      ),
      notes: (reorderHandleProps) => (
        <NotesCard
          fulfillmentGroup={fulfillmentGroup}
          isEditable={permissionsToEditEveryGroup && !isEditingLocked}
          reorderHandleProps={reorderHandleProps}
          showReorderHandle
          updateRows={handleUpdates}
        />
      ),
      "payment-information": (reorderHandleProps) => (
        <PaymentInformationCard
          fulfillmentGroup={fulfillmentGroup}
          reorderHandleProps={reorderHandleProps}
          showReorderHandle
        />
      ),
      "parcel-photos": (reorderHandleProps) => (
        <ParcelPhotosCard
          fulfillmentGroup={fulfillmentGroup}
          reorderHandleProps={reorderHandleProps}
          showReorderHandle
        />
      ),
    };

    const visibleShipmentDetailsCards: ShipmentDetailsCardData[] =
      shipmentDetailsCardOrder
        .filter((cardId) => {
          if (cardId === "parcel-photos") {
            return showParcelPhotos;
          }
          return true;
        })
        .map((cardId) => ({
          id: cardId,
          renderCard: shipmentDetailsCardRenderers[cardId],
        }));

    return (
      <PinVerificationProvider>
        <EditAddressModalProvider>
          <PackingSlipBinModalProvider>
            <MissingItemsModalProvider>
              <Flex
                className={styles.shipmentContainer}
                flexDirection="column"
                gap="3xl"
                p="3xl"
                pb="6xl"
                w="full"
              >
                {showMainFulfillmentForm && (
                  <GroupMainCard
                    everythingDisabled={!permissionsToEditEveryGroup}
                    fulfillmentGroup={fulfillmentGroup}
                    handleUpdates={handleUpdates}
                    index={index}
                    orderInfo={orderInfo}
                    selectedStatus={selectedStatus}
                    totalGroups={totalGroups}
                  />
                )}
                <Flex flexDirection="column" gap="none" w="full">
                  <DraggableList<
                    ShipmentDetailsCardData,
                    Record<string, never>,
                    DraggableShipmentDetailsCard
                  >
                    commonProps={{}}
                    constrainDrag
                    itemKey={(item) => item.id}
                    list={visibleShipmentDetailsCards}
                    onMoveEnd={handleShipmentDetailsCardReorder}
                    padding={shipmentDetailsCardDragPaddingPx}
                    template={DraggableShipmentDetailsCard}
                  />
                </Flex>
              </Flex>
            </MissingItemsModalProvider>
          </PackingSlipBinModalProvider>
        </EditAddressModalProvider>
      </PinVerificationProvider>
    );
  },
);

const GroupMainCard = memo(function GroupMainCard({
  fulfillmentGroup,
  index,
  totalGroups,
  selectedStatus,
  handleUpdates,
  everythingDisabled,
  orderInfo,
}: {
  fulfillmentGroup: FulfillmentGroupData;
  index: number;
  totalGroups: number;
  selectedStatus: FulfillmentGroupStatus | undefined | null;
  handleUpdates: (updates: FulfillmentGroupUpdate[]) => void;
  everythingDisabled: boolean;
  orderInfo: OrderDetailsInfo;
}) {
  return (
    <GroupMainCardHeader
      everythingDisabled={everythingDisabled}
      fulfillmentGroup={fulfillmentGroup}
      handleUpdates={handleUpdates}
      index={index}
      orderInfo={orderInfo}
      selectedStatus={selectedStatus}
      totalGroups={totalGroups}
    />
  );
});

const GroupMainCardHeader = memo(function GroupMainCardHeader({
  fulfillmentGroup,
  index,
  totalGroups,
  handleUpdates,
  selectedStatus,
  everythingDisabled,
  orderInfo,
}: {
  fulfillmentGroup: FulfillmentGroupData;
  index: number;
  selectedStatus: FulfillmentGroupStatus | undefined | null;
  totalGroups: number;
  handleUpdates: (updates: FulfillmentGroupUpdate[]) => void;
  everythingDisabled: boolean;
  orderInfo: OrderDetailsInfo;
}) {
  const rpcMerchantClient = useRequiredContext(RedoMerchantRpcClientContext);

  const [availableTagsLoad, getAvailableTags] = useTriggerLoad(
    async (signal: AbortSignal) => {
      try {
        const { tags } = await rpcMerchantClient.getTeamTags(
          { kind: TagKind.FULFILLMENT_GROUP },
          { signal },
        );
        return tags;
      } catch (e) {
        return [];
      }
    },
  );

  useEffect(() => {
    getAvailableTags();
    // FIXME
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deliveryStatus =
    fulfillmentGroup.fulfillmentInfo?.tracker?.status ??
    orderInfo.order?.trackers?.[0]?._tracker.status;
  const deliveryStatusBadge = deliveryStatus
    ? getDeliveryStatusBadge(deliveryStatus)
    : null;
  const marketplaceOrderTags = useMemo(
    () => getMarketplaceOrderTags(fulfillmentGroup),
    [fulfillmentGroup],
  );

  const [holdModalOpen, setHoldModalOpen] = useState(false);
  const openHoldModal = useCallback(() => setHoldModalOpen(true), []);
  const closeHoldModal = useCallback(() => setHoldModalOpen(false), []);

  const holds = fulfillmentGroup.holds;
  const isOnHold =
    fulfillmentGroup.status === FulfillmentGroupStatus.OnHold &&
    holds &&
    holds.length > 0;
  const firstHoldReason = isOnHold
    ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ShopifyFulfillmentHoldDisplayNames[holds[0]!.reason]
    : undefined;

  return (
    <>
      <RedoContentCard
        contentSpacing="compact"
        defaultExpanded
        headerSpacing="compact"
        title={
          <Flex align="center" className={styles.headerTitleRow} gap="md">
            <Text fontSize="lg" fontWeight="semibold" textColor="secondary">
              Fulfillment {index + 1} of {totalGroups}
            </Text>
            {deliveryStatusBadge}
            {!canPurchaseLabelStatus(selectedStatus) && (
              <>
                {isOnHold ? (
                  <RedoButton
                    hierarchy="secondary"
                    IconLeading={DotIcon}
                    onClick={openHoldModal}
                    size="xs"
                    text={`On hold: ${firstHoldReason}`}
                    theme="action"
                  />
                ) : (
                  <FulfillmentGroupStatusBadge
                    fulfillmentGroupStatus={fulfillmentGroup.status}
                  />
                )}
              </>
            )}
          </Flex>
        }
      >
        <Flex flexDirection="column" gap="3xl" w="full">
          <div className={styles.shipmentSummaryGrid}>
            <div className={styles.shipmentSummaryMetadataRow}>
              <ShipmentSummaryFact
                className={styles.shipmentSummaryTags}
                label="Redo fulfillment tag"
                value={
                  <Tags
                    availableTagsLoad={availableTagsLoad}
                    data={fulfillmentGroup}
                    disableOverflow
                    getAvailableTags={getAvailableTags}
                    updateData={handleUpdates}
                  />
                }
              />
              <ShipmentSummaryFact
                className={styles.shipmentSummaryTags}
                label="Marketplace order tags"
                value={
                  marketplaceOrderTags.length > 0 ? (
                    <TagList disableOverflow tags={marketplaceOrderTags} />
                  ) : (
                    "-"
                  )
                }
              />
            </div>
          </div>
        </Flex>
      </RedoContentCard>
      {isOnHold && (
        <RedoModal
          isOpen={holdModalOpen}
          onModalCloseRequested={closeHoldModal}
          primaryButton={{ text: "Close", onClick: closeHoldModal }}
          title="Hold Details"
        >
          <Flex flexDirection="column" gap="lg">
            {holds.map((hold, i) => (
              <Flex flexDirection="column" gap="xs" key={hold.holdHandle || i}>
                <Text fontSize="sm" fontWeight="semibold">
                  {ShopifyFulfillmentHoldDisplayNames[hold.reason]}
                </Text>
                {hold.reasonNotes && (
                  <Text fontSize="sm" textColor="secondary">
                    {hold.reasonNotes}
                  </Text>
                )}
              </Flex>
            ))}
          </Flex>
        </RedoModal>
      )}
    </>
  );
});

function ShipmentSummaryFact({
  className,
  label,
  value,
}: {
  className?: string;
  label: string;
  value: ReactNode;
}) {
  return (
    <Flex
      className={
        className
          ? `${styles.shipmentSummaryFact} ${className}`
          : styles.shipmentSummaryFact
      }
      flexDirection="column"
      gap="xs"
    >
      <Text fontSize="xs" textColor="secondary">
        {label}
      </Text>
      {typeof value === "string" ? (
        <Text className={styles.ellipsesOverflow} fontSize="xs">
          {value}
        </Text>
      ) : (
        value
      )}
    </Flex>
  );
}

function getDeliveryStatusBadge(status: string): ReactNode {
  if (status === "stalled_in_transit") {
    return (
      <RedoBadge
        color="warning"
        segmentLeading={{ type: "icon", Icon: DotIcon }}
        size="xs"
        text="Stalled"
        type="primary"
      />
    );
  }

  if (
    !Object.prototype.hasOwnProperty.call(prettyTrackerStatus, status) ||
    !Object.prototype.hasOwnProperty.call(trackerStatusColors, status)
  ) {
    return (
      <Text className={styles.ellipsesOverflow} fontSize="xs">
        {status}
      </Text>
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
  const trackerStatus = status as keyof typeof prettyTrackerStatus;
  return (
    <RedoBadge
      color={trackerStatusColors[trackerStatus]}
      segmentLeading={{ type: "icon", Icon: DotIcon }}
      size="xs"
      text={prettyTrackerStatus[trackerStatus]}
      type="primary"
    />
  );
}

function useShipmentPageContext(fulfillmentGroup: FulfillmentGroupData) {
  const { setPageContext } = useContext(GlobalAgentContext);
  const fulfillmentGroupId = fulfillmentGroup._id;
  const orderName = fulfillmentGroup.orders?.[0]?.name;

  useEffect(() => {
    const displayOrderName = orderName || "Unknown";
    setPageContext?.({
      pageTitle: `Order ${displayOrderName}`,
      pageDescription: `User is viewing fulfillment for order: ${displayOrderName}, fulfillment ID: ${fulfillmentGroupId}`,
      preferredAgent: "oms",
      preferredSkill: "oms",
      pageData: { fulfillmentGroupId, orderName: displayOrderName },
    });
    return () => {
      setPageContext?.(undefined);
    };
  }, [setPageContext, fulfillmentGroupId, orderName]);
}
